import React from "react";
import { Card, CardHeader, CardBody, Row, Col, Form, FormSelect, Button, Badge } from "shards-react";
import axios from "axios";

import baseURL from "../common/axios-config";
import { buildDougsSnippet, copyToClipboard } from "./dougsSnippet";


export default class GenerationInvoices extends React.Component {
	constructor(props) {
		super(props);
		const date = new Date();
		this.state = {
			yearArray: [date.getFullYear(), date.getFullYear() - 1, date.getFullYear() - 2],
			monthArray: [
				{ label: "Janvier", int: 0 },
				{ label: "Février", int: 1 },
				{ label: "Mars", int: 2 },
				{ label: "Avril", int: 3 },
				{ label: "Mai", int: 4 },
				{ label: "Juin", int: 5 },
				{ label: "Juillet", int: 6 },
				{ label: "Août", int: 7 },
				{ label: "Septembre", int: 8 },
				{ label: "Octobre", int: 9 },
				{ label: "Novembre", int: 10 },
				{ label: "Décembre", int: 11 },
			],
			accountArray: [],
			year: date.getFullYear(),
			month: date.getMonth(),
			account: "",
			invoices: [],
			rowState: {}, // { [client]: "copied" | "logged" }
			loading: false,
		};
	}

	componentDidMount() {
		this.getAccounts();
	}

	monthParam() {
		const m = parseInt(this.state.month) + 1;
		return m > 9 ? String(m) : "0" + m;
	}

	onYearChange = (e) => this.setState({ year: e.target.value, invoices: [], rowState: {} });
	onMonthChange = (e) => this.setState({ month: e.target.value, invoices: [], rowState: {} });
	onAccountChange = (e) => this.setState({ account: e.target.value, invoices: [], rowState: {} });

	getAccounts() {
		axios(baseURL + "/monthlyBilling-getAccounts", {
			method: "POST",
			data: { year: this.state.year, month: this.state.month },
			withCredentials: true,
		}).then((res) => {
			if (res.data.result === true) {
				this.setState({ accountArray: res.data.account });
			}
		});
	}

	getReports = () => {
		axios(baseURL + "/monthlyBilling-makeReports", {
			method: "POST",
			data: { year: this.state.year, month: this.monthParam(), account: this.state.account },
			withCredentials: true,
		}).then((res) => {
			if (res.data.result === true) {
				res.data.account.forEach((element_report) => {
					const downloadLink = document.createElement("a");
					downloadLink.href = element_report.base64;
					downloadLink.download = element_report.name + ".csv";
					downloadLink.click();
				});
			}
		});
	};

	// Step 1 — Prepare invoice data (server-side, no Dougs auth needed).
	// account === "__all__" prepares every billing account for the month.
	prepareInvoices = () => {
		this.setState({ loading: true });
		const all = this.state.account === "__all__";
		const url = all ? "/monthlyBilling-prepareAllInvoices" : "/monthlyBilling-prepareInvoices";
		const data = all
			? { year: this.state.year, month: this.monthParam() }
			: { year: this.state.year, month: this.monthParam(), account: this.state.account };
		axios(baseURL + url, { method: "POST", data, withCredentials: true }).then((res) => {
			if (res.data.result === true) {
				this.setState({ invoices: res.data.invoices || [], rowState: {}, loading: false });
			} else {
				this.setState({ loading: false });
			}
		});
	};

	buildPayload(inv) {
		const p = {
			subject: inv.subject,
			lines: [
				{
					title: inv.line_title,
					quantity: inv.credit,
					unitAmount: inv.unit_amount,
					vatRate: 0.2,
					discount: inv.discount_pct,
				},
			],
		};
		// Prefer the explicit SIRET client from config (exact, no ambiguity);
		// fall back to name search only when no `to`/SIRET is configured.
		if (inv.dougsClient) p.client = inv.dougsClient;
		else p.clientSearch = inv.clientSearch;
		return p;
	}

	// Log one invoice into the local tracking table (paid/relance/CSV). Optimistic —
	// removable from the invoices list if the Dougs creation is aborted.
	logInvoice(inv) {
		return axios(baseURL + "/monthlyBilling-logDougsInvoice", {
			method: "POST",
			data: {
				email: inv.account || this.state.account,
				client: inv.client,
				price: inv.ttc,
				credit: inv.credit,
				reference: "",
			},
			withCredentials: true,
		});
	}

	// Step 2 — one invoice: copy its snippet + log it
	generateOnDougs = (inv) => {
		copyToClipboard(buildDougsSnippet([this.buildPayload(inv)])).then(() => {
			this.setState((s) => ({ rowState: { ...s.rowState, [inv.client]: "copied" } }));
			this.logInvoice(inv).then((res) => {
				if (res.data.result === true) {
					this.setState((s) => ({ rowState: { ...s.rowState, [inv.client]: "logged" } }));
				}
			});
		});
	};

	// Step 2 (bulk) — ALL invoices in ONE snippet (single paste) + log them all
	generateAllOnDougs = () => {
		const billable = this.state.invoices.filter((i) => i.ttc > 0);
		if (billable.length === 0) return;
		copyToClipboard(buildDougsSnippet(billable.map((i) => this.buildPayload(i)))).then(() => {
			const rs = {};
			billable.forEach((i) => (rs[i.client] = "copied"));
			this.setState((s) => ({ rowState: { ...s.rowState, ...rs } }));
			Promise.all(billable.map((i) => this.logInvoice(i))).then(() => {
				const done = {};
				billable.forEach((i) => (done[i.client] = "logged"));
				this.setState((s) => ({ rowState: { ...s.rowState, ...done } }));
			});
		});
	};

	render() {
		const ready = this.state.year && this.state.month !== "" && this.state.account;
		const { invoices, rowState } = this.state;
		const totalTtc = invoices.reduce((acc, i) => acc + (i.ttc || 0), 0);

		return (
			<Card small className="mb-4">
				<CardHeader className="border-bottom">
					<h6 className="m-0">{this.props.title}</h6>
				</CardHeader>
				<CardBody>
					<Form className="w-100">
						<Row form>
							<Col lg="3" md="6" sm="12" className="form-group">
								<FormSelect value={this.state.year} onChange={this.onYearChange}>
									{this.state.yearArray.map((element, idx) => (
										<option key={idx} value={element}>{element}</option>
									))}
								</FormSelect>
							</Col>
							<Col lg="3" md="6" sm="12" className="form-group">
								<FormSelect value={this.state.month} onChange={this.onMonthChange}>
									{this.state.monthArray.map((element, idx) => (
										<option key={idx} value={element.int}>{element.label}</option>
									))}
								</FormSelect>
							</Col>
							<Col lg="3" md="6" sm="12" className="form-group">
								<FormSelect value={this.state.account} onChange={this.onAccountChange}>
									<option value="" disabled>Veuillez sélectionner un compte</option>
									<option value="__all__">— Tous les comptes —</option>
									{this.state.accountArray.map((element, idx) => (
										<option key={idx} value={element}>{element}</option>
									))}
								</FormSelect>
							</Col>
							<Col lg="3" md="6" sm="12" className="form-group d-flex">
								<Button className="flex-fill mr-2" theme="light" disabled={!ready} onClick={this.getReports}>
									Rapports CSV
								</Button>
								<Button className="flex-fill" theme="accent" disabled={!ready || this.state.loading} onClick={this.prepareInvoices}>
									{this.state.loading ? "…" : "Préparer les factures"}
								</Button>
							</Col>
						</Row>
					</Form>

					{invoices.length > 0 && (
						<div className="mt-2">
							<div className="mb-2 px-2 py-2" style={{ background: "#f5f6f8", borderRadius: 6, fontSize: "0.85rem" }}>
								<strong>Étape 2 — Générer sur Dougs.</strong> Ouvrez un onglet{" "}
								<a href="https://app.dougs.fr" target="_blank" rel="noopener noreferrer">app.dougs.fr</a>{" "}
								connecté. Utilisez <strong>« Générer tout sur Dougs »</strong> (un seul collage crée tous les
								brouillons) ou le bouton par ligne. Collez le script dans la console de l'onglet Dougs (F12) →
								les brouillons sont créés. Vérifiez puis finalisez dans Dougs.
							</div>
							<div className="mb-2 d-flex justify-content-end">
								<Button
									theme="accent"
									disabled={this.state.invoices.filter((i) => i.ttc > 0).length === 0}
									onClick={this.generateAllOnDougs}
								>
									Générer tout sur Dougs ({this.state.invoices.filter((i) => i.ttc > 0).length})
								</Button>
							</div>
							<table className="table mb-0">
								<thead className="bg-light">
									<tr>
										<th className="border-0">Client</th>
										<th className="border-0 text-right">Crédits</th>
										<th className="border-0 text-right">Remise</th>
										<th className="border-0 text-right">HT</th>
										<th className="border-0 text-right">TVA</th>
										<th className="border-0 text-right">TTC</th>
										<th className="border-0 text-center">Action</th>
									</tr>
								</thead>
								<tbody>
									{invoices.map((inv, idx) => (
										<tr key={idx}>
											<td style={{ fontWeight: 500 }}>
												{inv.client}
												<div style={{ fontSize: "0.72rem", fontWeight: 400 }}>
													{inv.siret ? (
														<span className="text-success">SIRET {inv.siret}</span>
													) : inv.dougsClient ? (
														<span className="text-secondary">client configuré</span>
													) : (
														<span className="text-warning">recherche par nom</span>
													)}
												</div>
											</td>
											<td className="text-right">{inv.credit}</td>
											<td className="text-right">{inv.discount_str}</td>
											<td className="text-right">{inv.ht.toFixed(2)} €</td>
											<td className="text-right">{inv.tva.toFixed(2)} €</td>
											<td className="text-right" style={{ fontWeight: 600 }}>{inv.ttc.toFixed(2)} €</td>
											<td className="text-center" style={{ whiteSpace: "nowrap" }}>
												<Button
													size="sm"
													theme={inv.ttc === 0 ? "light" : "accent"}
													disabled={inv.ttc === 0}
													onClick={() => this.generateOnDougs(inv)}
												>
													{rowState[inv.client] ? "Copier à nouveau" : "Générer sur Dougs"}
												</Button>
												{rowState[inv.client] === "copied" && (
													<Badge theme="info" className="ml-2">Script copié</Badge>
												)}
												{rowState[inv.client] === "logged" && (
													<Badge theme="success" className="ml-2">Copié + suivi ✓</Badge>
												)}
												{inv.ttc === 0 && <Badge theme="secondary" className="ml-2">0 € — ignoré</Badge>}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td colSpan="5" className="text-right" style={{ fontWeight: 600 }}>Total TTC</td>
										<td className="text-right" style={{ fontWeight: 700 }}>{totalTtc.toFixed(2)} €</td>
										<td></td>
									</tr>
								</tfoot>
							</table>
						</div>
					)}
				</CardBody>
			</Card>
		);
	}
}
